import React from 'react';
import { motion } from 'framer-motion';
import { Compass } from 'lucide-react';

const IMG_FOREST = 'https://customer-assets.emergentagent.com/job_b94b95c1-d586-44dc-829b-05989d33f0ec/artifacts/0ie4b4hx_Chapter%201%2001%20p1.jpg';
const IMG_LODGE = 'https://customer-assets.emergentagent.com/job_b94b95c1-d586-44dc-829b-05989d33f0ec/artifacts/era4n76p_Chapter%201%2001%20p2.jpg';
const IMG_TRAIL = 'https://customer-assets.emergentagent.com/job_b94b95c1-d586-44dc-829b-05989d33f0ec/artifacts/hdea1n9f_Chapter%201%2001%20p3.jpg';

const AboutSection = () => {
  return (
    <section id="about" className="relative bg-[#0B0F0E] py-32 md:py-44 overflow-hidden" data-testid="about-section">
      <div className="absolute -top-20 right-0 w-96 h-96 rounded-full bg-[#9E7A36]/[0.03] blur-3xl pointer-events-none" />

      <div className="max-w-[1480px] mx-auto px-6 md:px-12">
        {/* big chapter heading */}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 lg:col-span-3">
            <div className="overline">Chapter I</div>
            <div className="numeral text-7xl md:text-8xl text-[#D4AF37]/30 mt-4">01</div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-[#E8E3D9] leading-[1.05]"
            >
              The sanctuary is not<br/>
              a destination —<br/>
              <span className="italic text-[#D4AF37]">it is the address.</span>
            </motion.h2>
          </div>
        </div>

        {/* asymmetric photo + narrative grid */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">

          {/* photo column — staggered */}
          <div className="col-span-12 lg:col-span-7 relative h-[480px] md:h-[640px] order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.1 }}
              className="absolute top-0 left-0 w-[58%] h-[60%] overflow-hidden border border-[#D4AF37]/20"
            >
              <img src={IMG_FOREST} alt="Forest sanctuary" className="w-full h-full object-cover" data-testid="about-img-1" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
              className="absolute top-[18%] right-0 w-[52%] h-[55%] overflow-hidden border border-[#D4AF37]/30 shadow-2xl"
            >
              <img src={IMG_LODGE} alt="Resort" className="w-full h-full object-cover" data-testid="about-img-2" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.5 }}
              className="absolute bottom-0 left-[12%] w-[55%] h-[42%] overflow-hidden border border-[#D4AF37]/20"
            >
              <img src={IMG_TRAIL} alt="Trail" className="w-full h-full object-cover" data-testid="about-img-3" />
            </motion.div>

            {/* floating quote tag */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.8 }}
              className="absolute -bottom-6 right-2 md:right-12 bg-[#0B0F0E] border border-[#D4AF37]/40 px-5 py-3 max-w-[220px]"
            >
              <Compass size={14} className="text-[#D4AF37] mb-1" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#C4BFB1]">
                Tholpetty Wildlife Sanctuary, Kerala
              </p>
            </motion.div>
          </div>

          {/* narrative */}
          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
            >
              <p className="font-serif italic text-2xl md:text-3xl text-[#D4AF37] leading-tight mb-6">
                "Nature always wears the colours of your soul."
              </p>
              <div className="brass-rule w-24 my-6" />
              <p className="text-[#C4BFB1] leading-[1.85] mb-5 text-[15px]">
                Tucked into the unguarded edge of <span className="text-[#E8E3D9]">Tholpetty Wildlife Sanctuary</span>,
                Tuskermark is a nine-room resort where the forest is not a view — it is the wall, the ceiling,
                the dawn alarm.
              </p>
              <p className="text-[#8E9A95] leading-[1.85] text-[14px]">
                We host the kind of guest who measures luxury in birdcalls and elephant tracks.
                The kind who finds the smell of woodsmoke more intoxicating than perfume.
                Stay with us — slow down — and let the wild remember your name.
              </p>

              {/* mini stats */}
              <div className="mt-12 grid grid-cols-3 gap-4 border-t border-[#D4AF37]/15 pt-8">
                <div>
                  <div className="numeral text-2xl text-[#D4AF37]">09</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#8E9A95] mt-1">rooms</div>
                </div>
                <div>
                  <div className="numeral text-2xl text-[#D4AF37]">12+</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#8E9A95] mt-1">experiences</div>
                </div>
                <div>
                  <div className="numeral text-2xl text-[#D4AF37]">∞</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#8E9A95] mt-1">wild encounters</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
